import React, { useState } from "react";
import { ArrowSquareOut, ShieldCheck, FileText, Image as ImageIcon, Globe, PlayCircle, MagnifyingGlass, SealCheck } from "@phosphor-icons/react";
import { API } from "@/lib/api";

const CONF = {
  high: { label: "High confidence", cls: "text-[#73bf44] border-[#73bf44]/40 bg-[#73bf44]/10" },
  medium: { label: "General guidance", cls: "text-amber-400 border-amber-500/40 bg-amber-500/10" },
  low: { label: "Low confidence", cls: "text-neutral-400 border-neutral-600 bg-neutral-800/50" },
};

const TYPE_LABELS = {
  manual: "Owner's Manual",
  wiring_diagram: "Wiring Diagram",
  service_bulletin: "Service Bulletin",
  warranty: "Warranty",
  document: "Document",
  knowledge: "OTM Knowledge Base",
};

const typeLabel = (t) =>
  TYPE_LABELS[t] || (t ? t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Document");

const isViewableDoc = (s) => (s.kind ? s.kind === "document" : true) && s.viewable;
const isWebsite = (s) => (s.tier === "website" || s.kind === "website") && s.url;
const isWebSearch = (s) => s.tier === "web_search" && s.url;
const isVideo = (s) => s.kind === "video" && s.youtube_id;

export function splitSources(sources = []) {
  return {
    imageDocs: sources.filter((s) => isViewableDoc(s) && s.is_image),
    fileDocs: sources.filter((s) => isViewableDoc(s) && !s.is_image),
    videos: sources.filter(isVideo),
    website: sources.filter(isWebsite),
    webSearch: sources.filter(isWebSearch),
    referenced: sources.filter((s) => !isViewableDoc(s) && s.kind !== "video" && !isWebsite(s) && s.tier !== "web_search"),
  };
}

const docUrl = (id, token) => `${API}/documents/${id}/file${token ? `?auth=${token}` : ""}`;

function ImageAttachment({ s, token }) {
  const url = docUrl(s.id, token);
  const [broken, setBroken] = useState(false);
  if (broken) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-neutral-800 bg-[#101010] px-3 py-2.5 text-[0.75rem] text-neutral-500" data-testid="message-attachment-missing">
        <ImageIcon size={14} className="shrink-0" /> {s.title} — no longer available
      </div>
    );
  }
  return (
    <figure data-testid="message-attachment-image">
      <a href={url} target="_blank" rel="noreferrer" className="group block rounded-xl overflow-hidden border border-neutral-800 hover:border-[#73bf44] transition-colors bg-black">
        <img src={url} alt={s.title} onError={() => setBroken(true)} className="w-full max-h-80 object-contain bg-black" />
      </a>
      <figcaption className="flex items-center gap-1.5 mt-1 pl-0.5 text-[0.7rem] text-neutral-500">
        <ImageIcon size={12} className="shrink-0" />
        <span className="truncate">{s.title}</span>
        <a href={url} target="_blank" rel="noreferrer" data-testid="message-attachment-image-open" className="ml-auto inline-flex items-center gap-1 text-neutral-500 hover:text-[#73bf44] transition-colors shrink-0">
          <ArrowSquareOut size={11} /> Open
        </a>
      </figcaption>
    </figure>
  );
}

function FileAttachment({ s, token }) {
  return (
    <a
      href={docUrl(s.id, token)}
      target="_blank"
      rel="noreferrer"
      data-testid="message-attachment-file"
      className="group flex items-center gap-3 rounded-xl border border-neutral-800 bg-[#101010] hover:border-[#73bf44] px-3 py-2.5 transition-colors"
    >
      <div className="w-9 h-9 rounded-lg bg-[#73bf44]/10 border border-[#73bf44]/30 flex items-center justify-center shrink-0">
        <FileText size={18} className="text-[#73bf44]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-white truncate">{s.title}</div>
        <div className="text-[0.7rem] text-neutral-500">{typeLabel(s.type)}</div>
      </div>
      <span className="inline-flex items-center gap-1 text-[0.72rem] font-semibold text-[#73bf44] group-hover:underline shrink-0">
        View document <ArrowSquareOut size={12} />
      </span>
    </a>
  );
}

function VideoCard({ s }) {
  return (
    <a
      href={s.youtube_url || `https://www.youtube.com/watch?v=${s.youtube_id}`}
      target="_blank"
      rel="noreferrer"
      data-testid="message-video"
      className="group flex items-center gap-3 rounded-xl border border-neutral-800 bg-[#101010] hover:border-[#73bf44] p-2.5 transition-colors"
    >
      <div className="relative w-28 h-16 rounded-lg overflow-hidden shrink-0 bg-black">
        <img src={`https://img.youtube.com/vi/${s.youtube_id}/mqdefault.jpg`} alt={s.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/10 transition-colors">
          <PlayCircle size={30} weight="fill" className="text-white drop-shadow" />
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[0.62rem] font-bold uppercase tracking-wide text-[#73bf44]">Official OTM how-to video</div>
        <div className="text-sm font-medium text-white line-clamp-2">{s.title}</div>
        <span className="inline-flex items-center gap-1 text-[0.72rem] text-neutral-400 mt-0.5">
          Watch on YouTube <ArrowSquareOut size={11} />
        </span>
      </div>
    </a>
  );
}

function WebSearchResults({ items }) {
  return (
    <div className="rounded-xl border border-sky-500/25 bg-sky-500/[0.04] p-3" data-testid="message-web-search">
      <div className="flex items-center gap-1.5 text-[0.65rem] font-bold uppercase tracking-wide text-sky-300 mb-2">
        <MagnifyingGlass size={12} weight="bold" /> Found online
      </div>
      <div className="space-y-1.5">
        {items.map((s) => (
          <a key={s.id} href={s.url} target="_blank" rel="noreferrer" data-testid="message-source-websearch" className="group flex items-center gap-2 text-[0.78rem] text-neutral-300 hover:text-sky-300 transition-colors">
            {s.approved ? <SealCheck size={14} className="text-sky-300 shrink-0" weight="fill" /> : <Globe size={14} className="text-neutral-500 shrink-0" />}
            <span className="truncate">{s.title}</span>
            <span className="text-neutral-600 shrink-0">· {s.domain}</span>
            <ArrowSquareOut size={11} className="shrink-0 opacity-60 group-hover:opacity-100" />
          </a>
        ))}
      </div>
    </div>
  );
}

function ReferenceChips({ conf, website, referenced }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2 pl-1" data-testid="message-references">
      {conf && (
        <span className={`text-[0.65rem] font-semibold px-2 py-0.5 rounded-full border ${conf.cls}`} data-testid="message-confidence">
          {conf.label}
        </span>
      )}
      {website.map((s) => (
        <a key={s.id} href={s.url} target="_blank" rel="noreferrer" title={`Official OTM website — ${s.title}`} data-testid="message-source-web" className="inline-flex items-center gap-1.5 text-[0.7rem] px-2.5 py-1 rounded-full border border-[#73bf44]/40 text-[#73bf44] hover:bg-[#73bf44]/10 transition-colors max-w-[260px]">
          <Globe size={12} className="shrink-0" /> <span className="truncate">OTM website · {s.title}</span> <ArrowSquareOut size={10} className="shrink-0" />
        </a>
      ))}
      {referenced.map((s) => (
        <span key={s.id} className="inline-flex items-center gap-1.5 text-[0.7rem] px-2.5 py-1 rounded-full border border-neutral-800 text-neutral-500" data-testid="message-source-label">
          <ShieldCheck size={12} /> {s.title}
        </span>
      ))}
    </div>
  );
}

export function MessageAttachments({ sources, confidence, token }) {
  const g = splitSources(sources || []);
  const conf = CONF[confidence] || null;
  const hasDocs = g.imageDocs.length > 0 || g.fileDocs.length > 0;
  const hasChips = conf || g.website.length > 0 || g.referenced.length > 0;
  return (
    <>
      {hasDocs && (
        <div className="mt-3 space-y-3" data-testid="message-attachments">
          {g.imageDocs.map((s) => <ImageAttachment key={s.id} s={s} token={token} />)}
          {g.fileDocs.map((s) => <FileAttachment key={s.id} s={s} token={token} />)}
        </div>
      )}
      {g.videos.length > 0 && (
        <div className="mt-3 space-y-2.5" data-testid="message-videos">
          {g.videos.map((s) => <VideoCard key={s.id} s={s} />)}
        </div>
      )}
      {g.webSearch.length > 0 && <div className="mt-3"><WebSearchResults items={g.webSearch} /></div>}
      {hasChips && <ReferenceChips conf={conf} website={g.website} referenced={g.referenced} />}
    </>
  );
}
